// RGR10 반품모니터링 — moms-be 호출부. 1.0 공통 그리드(/api/grid/* · /api/common/*)는 쓰지 않는다.
// 다운로드 사유코드 콤보는 레포 훅 `useGetExportCombo` 가 담당한다(페이지에서 배선, 여기서는 다루지 않는다).
// D7 예외였던 1.0 `/api/common/combo` 는 LOGISPRD-14161 에서 2.0 `/api/v1/combos` 로 옮겼다.
import qs from 'qs'
import request, { getExcel } from '@/utils/request'
import {
  ReturnMonitoringDownloadReq,
  ReturnMonitoringItemDto,
  ReturnMonitoringSaveReq,
  ReturnMonitoringSaveRes,
  ReturnMonitoringSearchReq,
  ReturnMonitoringStatusSummaryDto,
  ReturnMonitoringWithdrawReq,
  ReturnMonitoringWithdrawRes,
} from './types'

export const RETURN_MONITORING_URL = '/api/v1/return/monitoring'
export const RETURN_MONITORING_SUMMARY_URL = '/api/v1/return/monitoring/summary'
export const RETURN_MONITORING_SAVE_URL = '/api/v1/return/monitoring/save'
export const RETURN_MONITORING_WITHDRAW_URL = '/api/v1/return/monitoring/withdraw'
export const RETURN_MONITORING_DOWNLOAD_URL = '/api/v1/return/monitoring/download'

/**
 * `rtnInvoiceNo`·`ordNo` 는 다건이라 `key=a&key=b` 로 편다 — BE 가 `List<String>` 으로 받는다
 * (returnReceipt/apis.ts 선례). 공용 `Utils.qsStringify` 는 `arrayFormat: 'comma'` 라 이 계약에 맞지 않는다.
 */
const toQuery = (filter: ReturnMonitoringSearchReq): string =>
  qs.stringify(
    {
      dateType: filter.dateType,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      shipper: filter.shipper,
      rtnInvoiceNo: filter.rtnInvoiceNo,
      actFlag: filter.actFlag,
      ordNo: filter.ordNo,
      ordStatus: filter.ordStatus,
    },
    { arrayFormat: 'repeat', skipNulls: true, filter: (_, value) => (value === '' ? undefined : value) },
  )

/** 조회 — 서버 페이징 없이 조건에 걸린 전건. 0건은 200 + 빈 배열. */
export const getReturnMonitoringItems = (filter: ReturnMonitoringSearchReq) =>
  request.get<ApiResponseV2<{ items: ReturnMonitoringItemDto[] }>>(`${RETURN_MONITORING_URL}?${toQuery(filter)}`)

/** 상태별 건수 — `ordStatus` 를 뺀 7종 필터. 탭을 바꿔도 값이 같으므로 조회와 별도 시점에만 호출한다. */
export const getReturnMonitoringStatusSummaries = (filter: Omit<ReturnMonitoringSearchReq, 'ordStatus'>) =>
  request.get<ApiResponseV2<{ items: ReturnMonitoringStatusSummaryDto[] }>>(`${RETURN_MONITORING_SUMMARY_URL}?${toQuery(filter)}`)

/** 저장 — I/E 전용. D 는 BE 가 400. */
export const saveReturnMonitoring = (params: ReturnMonitoringSaveReq) =>
  request.post<ApiResponseV2<ReturnMonitoringSaveRes>, ReturnMonitoringSaveReq>(RETURN_MONITORING_SAVE_URL, params)

/** 반품철회 — 접수완료 + 화주 일치 + 미삭제 건만 DB CAS 로 전이. 조건 밖 행은 조용히 스킵(오류 아님). */
export const withdrawReturnMonitoring = (params: ReturnMonitoringWithdrawReq) =>
  request.post<ApiResponseV2<ReturnMonitoringWithdrawRes>, ReturnMonitoringWithdrawReq>(RETURN_MONITORING_WITHDRAW_URL, params)

/** Content-Disposition 의 filename*=utf-8'' 값을 꺼낸다. 없으면 폴백 이름을 쓴다. */
const parseFileName = (disposition: string, fallback: string): string => {
  const utf8 = /filename\*=utf-8''([^;]+)/i.exec(disposition)
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim())
    } catch {
      /* 디코딩 실패 시 폴백 */
    }
  }
  const ascii = /filename="?([^";]+)"?/i.exec(disposition)
  return ascii?.[1]?.trim() || fallback
}

/**
 * 엑셀 다운로드 — 서버 생성 AES-256 암호 zip. POST + JSON 바디(쿼리스트링 아님 — `@RequestBody`).
 * 400(비밀번호 규칙·상세사유 5자·10만행 초과)의 서버 메시지를 그대로 노출한다(피드백 13) —
 * `responseType: 'blob'` 이라 에러 바디도 Blob 으로 오므로 JSON 으로 되돌린다(GL01 `consolOutbound/apis.ts` 선례).
 */
export const downloadReturnMonitoringExcel = async (params: ReturnMonitoringDownloadReq): Promise<void> => {
  let res
  try {
    res = await getExcel(RETURN_MONITORING_DOWNLOAD_URL, { method: 'post', data: params })
  } catch (e) {
    const resp = (e as { response?: { data?: unknown } })?.response
    if (resp?.data instanceof Blob) {
      try {
        resp.data = JSON.parse(await resp.data.text())
      } catch {
        /* JSON 이 아니면 상태코드 폴백을 그대로 둔다 */
      }
    }
    throw e
  }

  const fileName = parseFileName(String(res.headers?.['content-disposition'] ?? ''), '반품모니터링.zip')
  const blobUrl = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  a.click()
  // a.href 를 getter 로 다시 읽지 않고 생성 시 저장해 둔 blobUrl 로 revoke한다(브라우저가 절대경로로
  // 정규화해 돌려주는 구현이 있다). click() 이 트리거한 다운로드가 시작될 시간을 준 뒤 해제한다.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
}

/** axios 에러 응답에서 BE `meta.message` 를 꺼낸다. 없으면 기본 문구. */
export const apiErrMessage = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data
  return data?.meta?.message || data?.message || fallback
}
